import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GetMyTasksPayload } from '@/domains/tasks/features/myTaskList/slice/types/MyTasksState';
import type { EdsStoredSorting } from '@/features/eds/types/EdsSortingConfig';
import type { EDSFiltersRequestFiltersList } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';

type TaskListState = {
  taskList: NormalizedV2Response[];
  pagination: {
    page: number;
    perPage: number;
    isLastPage: boolean;
  };
  isLoading: boolean;
  sorting: EdsStoredSorting;
  filters: EDSFiltersRequestFiltersList;
  counters: {
    allTasks: number;
    myTasks: number;
  };
};

const initialState: TaskListState = {
  taskList: [],
  pagination: {
    page: 1,
    perPage: 20,
    isLastPage: true,
  },
  isLoading: false,
  sorting: { sortBy: null, sortDirection: null },
  filters: [],
  counters: {
    allTasks: 0,
    myTasks: 0,
  },
};

const taskListSlice = createSlice({
  name: 'taskListSlice',
  initialState,
  reducers: {
    setTaskListCounters: (state, { payload }: PayloadAction<TaskListState['counters']>) => {
      state.counters = payload;
    },
    setTaskListSorting(state, { payload }: PayloadAction<EdsStoredSorting>) {
      state.sorting = payload;
    },
    setTaskListFilters(state, { payload }: PayloadAction<EDSFiltersRequestFiltersList>) {
      state.filters = payload;
    },
    resetTaskListFilters(state) {
      state.filters = initialState.filters;
    },
    resetTaskListSorting(state) {
      state.sorting = initialState.sorting;
    },
    incrementTaskListPage(state, _action: PayloadAction<GetMyTasksPayload>) {
      if (!state.pagination.isLastPage) {
        state.pagination.page++;
        state.isLoading = true;
      }
    },
    resetTaskList(state) {
      state.pagination = initialState.pagination;
      state.taskList = [];
    },
    getTaskList(state, _action: PayloadAction<GetMyTasksPayload>) {
      state.isLoading = true;
      state.pagination = initialState.pagination;
    },
    getTaskListSuccess(
      state,
      { payload }: PayloadAction<{ taskList: NormalizedV2Response[]; pagination: TaskListState['pagination'] }>,
    ) {
      state.isLoading = false;

      state.pagination = payload.pagination;

      if (payload.pagination.page === 1) {
        state.taskList = payload.taskList;
      } else {
        state.taskList = [...state.taskList, ...payload.taskList];
      }
    },
  },
});

export const {
  getTaskList,
  getTaskListSuccess,
  incrementTaskListPage,
  resetTaskList,
  setTaskListFilters,
  setTaskListSorting,
  resetTaskListFilters,
  resetTaskListSorting,
  setTaskListCounters,
} = taskListSlice.actions;
export const taskListReducer = taskListSlice.reducer;
export const taskListReducerPath = taskListSlice.reducerPath;
